import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Dialog } from 'src/components/Dialog';
import { Typography } from 'src/components/Typography';
import {
  checkSetupIntentStatus,
  processTerminalSetupIntent,
} from 'src/models/cart/cart';
import { fetchSetupIntent } from 'src/models/payment/payment';
import { Terminal } from 'src/models/terminal/_types';
import { getTerminals } from 'src/models/terminal/terminal';

import { List, ListItem } from './ListItem';

import type { User } from 'src/models/user/_types';

interface TerminalPaymentMethodFormProps {
  open: boolean;
  onClose: () => void;
  user: User;
  handlePaymentMethodAdded: (result: any) => void;
}

export const TerminalPaymentMethodForm: React.FC<
  TerminalPaymentMethodFormProps
> = ({ open, onClose, user, handlePaymentMethodAdded }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'terminalPayment' });
  const [selectedReader, setSelectedReader] = useState<Terminal | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const { data: terminals, isLoading: isLoadingTerminals } = useQuery({
    queryKey: ['terminals'],
    queryFn: () => getTerminals({}),
    select: (data) => data.filter((terminal) => terminal.status === 'online'),
  });

  useEffect(() => {
    if (!user || !open) return;

    const fetchSecret = async () => {
      try {
        const result = await fetchSetupIntent({
          userId: user.id,
          paymentMethodTypes: ['card_present'],
        });
        setClientSecret(result.id);
      } catch (error) {
        setReadError(t('failedToInitialize'));
      }
    };

    fetchSecret();
  }, [user, open, t]);

  const handleClose = useCallback(() => {
    setSelectedReader(null);
    setIsReading(false);
    setReadError(null);
    setClientSecret(null);
    setIsPolling(false);
    onClose();
  }, [onClose]);

  const dialogActions = [
    {
      label: t('cancel'),
      onClick: handleClose,
    },
  ];

  const pollSetupIntentStatus = useCallback(
    async (setupIntentId: string) => {
      setIsPolling(true);
      let isActive = true;
      try {
        while (isActive) {
          const result = await checkSetupIntentStatus(setupIntentId);

          if (result.status === 'succeeded') {
            handlePaymentMethodAdded(result);
            handleClose();
            break;
          } else if (result.status === 'canceled') {
            setReadError('Card reading failed or was canceled');
            break;
          }

          // Wait 2 seconds before next poll
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      } catch (error) {
        setReadError(
          error instanceof Error ? error.message : 'Failed to check card status'
        );
      } finally {
        setIsPolling(false);
      }

      return () => {
        isActive = false;
      };
    },
    [handlePaymentMethodAdded, handleClose]
  );

  const handleCardReading = useCallback(
    async (terminal?: Terminal) => {
      if (!terminal || !clientSecret) return;

      const readerToUse = terminal || selectedReader || terminals?.[0];
      if (!readerToUse) return;

      try {
        setIsReading(true);
        setReadError(null);
        setSelectedReader(readerToUse);

        if (!readerToUse.stripeId) {
          setReadError('Terminal ID is missing');
          return;
        }

        // Process the setup intent with the terminal
        await processTerminalSetupIntent(readerToUse.stripeId, clientSecret);

        // Start polling for status
        await pollSetupIntentStatus(clientSecret);
      } catch (error) {
        setReadError(
          error instanceof Error ? error.message : 'Failed to read card'
        );
      } finally {
        setIsReading(false);
      }
    },
    [clientSecret, selectedReader, terminals, pollSetupIntentStatus]
  );

  useEffect(() => {
    if (
      clientSecret &&
      !isReading &&
      !selectedReader &&
      terminals?.length === 1
    ) {
      handleCardReading(terminals[0]);
    }
  }, [clientSecret, isReading, selectedReader, terminals, handleCardReading]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('swipeCard')}
      actions={dialogActions}
      fullWidth
    >
      {isLoadingTerminals ? (
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size="medium" />
          <Typography>Loading terminals...</Typography>
        </Box>
      ) : terminals && terminals.length > 1 && !selectedReader ? (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {t('selectReader')}
          </Typography>
          <List>
            {terminals.map((reader) => (
              <ListItem
                key={reader.stripeId}
                onClick={() => handleCardReading(reader)}
                primary={reader.label}
                clickable
              />
            ))}
          </List>
        </Box>
      ) : (
        <Box>
          <Typography sx={{ mb: 2 }}>
            {t('swipeCardOn', {
              reader:
                selectedReader?.label ||
                terminals?.[0]?.label ||
                t('theReader'),
            })}
          </Typography>
          {(isReading || isPolling) && (
            <Box display="flex" alignItems="center" gap={2}>
              <CircularProgress size="medium" />
              <Typography color="primary">{t('waitingForSwipe')}</Typography>
            </Box>
          )}
          {readError && (
            <Typography color="error" sx={{ mt: 1 }}>
              {readError}
            </Typography>
          )}
          {!isReading &&
            !isPolling &&
            !selectedReader &&
            terminals?.length === 1 &&
            !clientSecret && (
              <Box display="flex" alignItems="center" gap={2}>
                <CircularProgress size="medium" />
                <Typography>{t('initializingReader')}</Typography>
              </Box>
            )}
        </Box>
      )}
    </Dialog>
  );
};
